// src/components/union-orders/SelectedOrderDetails.tsx

import { useEffect, useMemo, useState } from 'react'
import { Box, HStack, Spinner, Tabs, Text, VStack, Button } from '@chakra-ui/react'
import { toast } from '@/components/ui/toaster/toasterHelpers'

import UnionOrderItem, { type OrderActionMode } from './UnionOrderItem'
import type { CapacityInfo } from './CheckCapacityPopup'
import { OrderReviewActionBar } from './OrderReviewActionBar'
import { LuCheckCheck, LuTextSelect } from 'react-icons/lu'
import { getBanks, getUserOrderCapacity } from '@/api/settings.api'
import { approveOrder, getOrders, getWorkload } from '@/api/orders.api'
import type { Order, OrderStatus } from '@/common/order.common'
import type { Bank } from '@/common/settings.common'
import { resolveOrderCustomerName } from '@/helper/order.helper'

export type UnionTabKey = 'OPEN' | 'RELEASED' | 'REJECTED' | 'ARCHIVE'

const TABS = [
  {
    key: 'OPEN' as UnionTabKey,
    label: 'Offen',
    filter: (o: Order) => ['SUBMITTED', 'CORRECTED'].includes(o.status),
  },
  {
    key: 'RELEASED' as UnionTabKey,
    label: 'Freigegeben',
    filter: (o: Order) => o.status === 'APPROVED',
  },
  {
    key: 'REJECTED' as UnionTabKey,
    label: 'Abgelehnt',
    filter: (o: Order) => o.status === 'REJECTED',
  },
  {
    key: 'ARCHIVE' as UnionTabKey,
    label: 'Archiv',
    filter: (o: Order) => o.status === 'COMPLETED',
  },
]

export interface SelectedOrderDetailsProps {
  sibId: string
  year: number
  sibName: string
  onRefreshOrders: () => void
  onExpandedOrderChange?: (order: Order | null) => void
  remainingCurrentYearUnits: CapacityInfo
}

/**
 * SelectedOrderDetails
 * =====================
 * The main widget for Union Dashboard "Order Details".
 */
export default function SelectedOrderDetails({
  sibId,
  year,
  sibName,
  onRefreshOrders,
  onExpandedOrderChange,
  remainingCurrentYearUnits,
}: SelectedOrderDetailsProps) {
  const [activeTab, setActiveTab] = useState<UnionTabKey>('OPEN')
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(false)
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null)
  const [reloadFlag, setReloadFlag] = useState(0)
  const [actionMode, setActionMode] = useState<OrderActionMode>('default')
  const [isApproving, setIsApproving] = useState(false)
  function getErrorMessage(err: any) {
    if (!err) return 'Unbekannter Fehler'
    if (typeof err === 'string') return err
    if (err.message) return err.message
    if (err.response?.data?.message) return err.response.data.message
    return JSON.stringify(err)
  }

  const [banks, setBanks] = useState<Bank[]>([])
  const unionBank = useMemo(
    () => banks.find(b => b.id === 1 || (b.name ?? '').toLowerCase().includes('union investment')),
    [banks]
  )
  const unionAssociatedEntities = unionBank?.associatedEntities ?? []

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await getBanks()
        if (!cancelled) setBanks(res.items ?? [])
      } catch {
        // optional: toast.error('Fehler', 'Banken konnten nicht geladen werden.')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])
  // -------------------------------------------------------
  // Load Orders
  // -------------------------------------------------------
  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        setLoading(true)
        const data = await getOrders({ userId: sibId, year: [year] })

        if (!cancelled) {
          const tab = TABS.find(t => t.key === activeTab)
          const yearItems = data.items?.[year] ?? {}

          const all = Object.values(yearItems).flat()

          const filtered = tab ? all.filter(tab.filter) : all

          const sorted = [...filtered].sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
          setOrders(sorted)
          const first = sorted[0] ?? null
          setExpandedOrderId(first?.id ?? null)
          onExpandedOrderChange?.(first)
          setActionMode('default')
        }
      } catch (err: any) {
        toast.error('Fehler', getErrorMessage(err))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [activeTab, sibId, year, reloadFlag])

  // -------------------------------------------------------
  // Capacity for the SIB user
  // -------------------------------------------------------
  const [capacityInfo, setCapacityInfo] = useState<CapacityInfo>({
    TOJ: 0,
    CONSULTING: 0,
    TRAINING: 0,
    PILOT: 0,
  })

  useEffect(() => {
    let cancelled = false

    async function loadCapacity() {
      try {
        const res = await getUserOrderCapacity(sibId)
        if (cancelled) return

        const mapped: CapacityInfo = {
          TOJ: 0,
          CONSULTING: 0,
          TRAINING: 0,
          PILOT: 0,
        }

        res.settings.forEach(s => {
          switch (s.service.code) {
            case 'TOJ':
              mapped.TOJ = s.capacity
              break
            case 'CONSULTING':
              mapped.CONSULTING = s.capacity
              break
            case 'TRAINING':
              mapped.TRAINING = s.capacity
              break
            case 'PILOT':
              mapped.PILOT = s.capacity
              break
          }
        })

        setCapacityInfo(mapped)
      } catch {
        toast.error('Fehler', 'Kapazitäten konnten nicht geladen werden.')
      }
    }

    loadCapacity()
    return () => {
      cancelled = true
    }
  }, [sibId])

  // -------------------------------------------------------
  // Workload for the selected year
  // -------------------------------------------------------
  const [, setWorkloadInfo] = useState<CapacityInfo>({
    TOJ: 0,
    CONSULTING: 0,
    TRAINING: 0,
    PILOT: 0,
  })

  useEffect(() => {
    let cancelled = false

    async function loadWorkloadYear() {
      try {
        const fromIso = `${year}-01-01T00:00:00.000`
        const toIso = `${year}-12-31T23:59:59.999`

        const res = await getWorkload({ userId: sibId, from: fromIso, to: toIso })
        if (cancelled) return

        const mapped: CapacityInfo = {
          TOJ: 0,
          CONSULTING: 0,
          TRAINING: 0,
          PILOT: 0,
        }

        res.buckets.forEach(b => {
          const val = b.completedOrdersHours ?? b.approvedOrdersHours ?? 0

          switch (b.serviceType) {
            case 'TOJ':
              mapped.TOJ += val
              break
            case 'CONSULTING':
              mapped.CONSULTING += val
              break
            case 'TRAINING':
              mapped.TRAINING += val
              break
            case 'PILOT':
              mapped.PILOT = (mapped.PILOT ?? 0) + val
              break
          }
        })

        setWorkloadInfo(mapped)
      } catch {
        toast.error('Fehler', 'Workload konnte nicht geladen werden.')
      }
    }

    loadWorkloadYear()
    return () => {
      cancelled = true
    }
  }, [sibId, year])

  function handleStatusChange(index: number, newStatus: string) {
    const updatedOrders = [...orders]
    updatedOrders[index] = {
      ...updatedOrders[index],
      status: newStatus as OrderStatus,
    }
    setOrders(updatedOrders)
  }
  // const remainingCurrentYear = {
  //   TOJ: capacityInfo.TOJ - workloadInfo.TOJ,
  //   CONSULTING: capacityInfo.CONSULTING - workloadInfo.CONSULTING,
  //   TRAINING: capacityInfo.TRAINING - workloadInfo.TRAINING,
  // };

  // const selectedOrder = orders.find(o => o.id === expandedOrderId) ?? null

  // const { remainingCurrentYear, remainingAfterApproval, useAllOpenOrders, setUseAllOpenOrders } =
  //   useSibCapacity({
  //     selectedOrder,
  //     allOrders: orders,
  //     agreedCapacity: capacityInfo,
  //     workloadInfo,
  //   })
  const allOrdersCompleted =
    orders.length > 0 && orders.every(o => ['APPROVED', 'REJECTED'].includes(o.status))

  const expandedOrder = orders.find(o => o.id === expandedOrderId) ?? null
  const canReview = activeTab === 'OPEN' && expandedOrder?.status === 'SUBMITTED'

  const handleToggleExpand = (order: Order) => {
    const next = expandedOrderId === order.id ? null : order
    setExpandedOrderId(next?.id ?? null)
    onExpandedOrderChange?.(next)
    setActionMode('default')
  }

  async function handleApprove() {
    if (!expandedOrder || isApproving) return
    try {
      setIsApproving(true)
      await approveOrder(expandedOrder.id)
      toast.success('Bestellung akzeptiert', 'Die Bestellung wurde erfolgreich freigegeben.')
      setActionMode('default')
      setReloadFlag(prev => prev + 1)
      onRefreshOrders()
    } catch (err) {
      toast.error('Fehler', getErrorMessage(err))
    } finally {
      setIsApproving(false)
    }
  }

  return (
    <HStack align="flex-start" gap={6} w="full">
      <Box flex="1">
        {/* Title */}
        <Text fontSize="md" fontWeight="normal" mb={3}>
          Bestellungen von {sibName}
        </Text>

        {/* Tabs */}
        <Tabs.Root
          value={activeTab}
          onValueChange={details => {
            setActiveTab(details.value as UnionTabKey)
            setActionMode('default')
          }}
          size="sm"
          variant="subtle"
          mb={3}
        >
          <Tabs.List gap="1.5" bg="bg.muted" borderRadius="sm" w="fit-content">
            {TABS.map(t => (
              <Tabs.Trigger
                key={t.key}
                value={t.key}
                bg="bg.muted"
                color="fg"
                px="4"
                py="2"
                _selected={{
                  bg: 'bg',
                  color: 'fg.default',
                  shadow: 'sm',
                }}
                _hover={{
                  bg: 'bg',
                  color: 'fg.default',
                }}
              >
                {t.label}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
        </Tabs.Root>

        {/* Main card */}
        <Box bg="bg" p={2}>
          {allOrdersCompleted && activeTab === 'OPEN' && (
            <HStack
              justify="flex-end"
              mt={4}
              p={4}
              borderTop="1px solid"
              borderColor="border.muted"
            >
              <Button
                bg="gray.subtle"
                variant="subtle"
                onClick={async () => {
                  try {
                    // Complete each APPROVED or REJECTED order
                    const toComplete = orders.filter(
                      o => o.status === 'APPROVED' || o.status === 'REJECTED'
                    )

                    for (const order of toComplete) {
                      await fetch(`/api/v1/orders/${order.id}/complete`, {
                        method: 'POST',
                        credentials: 'include',
                      })
                    }

                    toast.success(
                      'Prozess abgeschlossen',
                      'Alle Bestellungen dieses SIB wurden archiviert.'
                    )

                    setActiveTab('ARCHIVE')
                  } catch (e: any) {
                    toast.error(
                      'Fehler',
                      e.message ?? 'Prozess konnte nicht abgeschlossen werden.'
                    )
                  }
                }}
              >
                <LuCheckCheck /> Bestellung abschließen
              </Button>

              <Button
                // colorPalette="gray"
                variant="solid"
                onClick={() => {
                  setOrders([])
                  setExpandedOrderId(null)
                  onExpandedOrderChange?.(null)
                  setActiveTab('OPEN')
                  toast.info('Nächster SIB', 'Wechsel zum nächsten SIB.')
                }}
              >
                Nächster SIB
              </Button>
            </HStack>
          )}

          {loading && (
            <HStack justify="center" py={8}>
              <Spinner />
              <Text fontSize="sm" color="fg.muted">
                Bestellungen werden geladen …
              </Text>
            </HStack>
          )}

          {!loading && orders.length === 0 && (
            <VStack align="center" justify="center" w="full" py={20} gap={4}>
              <LuTextSelect size={32} color="var(--chakra-colors-fg-muted)" />
              <Text fontSize="xl" fontWeight="semibold">
                Keine Bestellung ausgewählt
              </Text>
              <Text fontSize="sm" textAlign="center" color="fg.muted">
                Bitte wählen Sie zunächst einen SIB aus der Liste oben aus. Seine Bestellungen für
                das Kalenderjahr werden hier angezeigt.
              </Text>
            </VStack>
          )}

          {!loading && orders.length > 0 && (
            <VStack align="stretch" gap={3}>
              {renderGroupedOrders({
                orders,
                expandedOrderId,
                onToggleExpand: handleToggleExpand,
                activeTab,
                actionMode,
                onModeChange: setActionMode,
                onStatusChange: handleStatusChange,
                capacityInfo,
                remainingCurrentYearUnits,
                triggerReload: () => setReloadFlag(prev => prev + 1),
                onRefreshOrders,
                unionAssociatedEntities,
              })}
            </VStack>
          )}
        </Box>
      </Box>

      {canReview && expandedOrder && (
        <OrderReviewActionBar
          isApproving={isApproving}
          onApprove={handleApprove}
          onCorrect={() => setActionMode('correction')}
          onReject={() => setActionMode('reject')}
        />
      )}
    </HStack>
  )
}

/**
 * Group by date and render order items with headers
 */
function renderGroupedOrders(opts: {
  orders: Order[]
  expandedOrderId: number | null
  onToggleExpand: (order: Order) => void
  activeTab: UnionTabKey
  actionMode: OrderActionMode
  onModeChange: (mode: OrderActionMode) => void
  onStatusChange: (index: number, newStatus: string) => void
  capacityInfo: CapacityInfo
  remainingCurrentYearUnits: CapacityInfo
  triggerReload: () => void
  onRefreshOrders: () => void
  unionAssociatedEntities?: {
    id: number
    name: string
    name2?: string | null
    name3?: string | null
  }[]
}) {
  const {
    orders,
    expandedOrderId,
    onToggleExpand,
    activeTab,
    actionMode,
    onModeChange,
    onStatusChange,
    capacityInfo,
    remainingCurrentYearUnits,
    triggerReload,
    onRefreshOrders,
    unionAssociatedEntities = [],
  } = opts

  let lastDate = ''

  return orders.map((order, index) => {
    const createdAt = new Date(order.createdAt)
    const dateLabel = new Intl.DateTimeFormat('de-DE').format(createdAt)

    const showHeader = dateLabel !== lastDate
    lastDate = dateLabel

    const isExpanded = expandedOrderId === order.id
    const displayName = resolveOrderCustomerName(order, unionAssociatedEntities)

    return (
      <Box key={order.id}>
        {showHeader && (
          <Text fontSize="sm" fontWeight="normal" color="fg.muted" mb={2} mt={lastDate ? 4 : 0}>
            {dateLabel}
          </Text>
        )}

        <UnionOrderItem
          order={order}
          bankName={displayName}
          isExpanded={isExpanded}
          onToggleExpand={() => onToggleExpand(order)}
          activeTab={activeTab}
          mode={isExpanded ? actionMode : 'default'}
          onModeChange={onModeChange}
          onStatusChange={newStatus => onStatusChange(index, newStatus)}
          capacityInfo={capacityInfo}
          onRefresh={() => {
            triggerReload()
            onRefreshOrders()
          }}
          remainingCurrentYearUnits={remainingCurrentYearUnits}
        />
      </Box>
    )
  })
}
